import { infiniteQueryOptions, keepPreviousData, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getCustomerBalance, getCustomerOptions } from '../redux/actions/customers';
import { setHasSingleCustomer } from '../redux/slices/customersSlice';
import { setCustomer } from '../redux/slices/filtersSlice';
import { store } from '../redux/store';
import { customerKeys } from './keys';

const OPTIONS_STALE_TIME = 5 * 60 * 1000;
const INSTANT_RESULTS_LIMIT = 50;

export const filterCustomers = (customers, text) => {
    const needle = text.trim().toLowerCase();
    if (!needle) return [];

    return customers
        .filter((c) => String(c.title ?? '').toLowerCase().includes(needle) || String(c.code ?? '').toLowerCase().includes(needle))
        .slice(0, INSTANT_RESULTS_LIMIT);
};

export const mergeCustomers = (...lists) => {
    const seen = new Set();
    return lists.flat().filter((c) => !seen.has(c.customer_netsuite_id) && seen.add(c.customer_netsuite_id));
};

const fetchOptions = async (params, signal) => {
    const res = await getCustomerOptions(params, signal);
    if (!res?.ack) throw new Error('Unable to load customers');
    return res;
};

export const customerOptionsQuery = (search = '') =>
    infiniteQueryOptions({
        queryKey: customerKeys.options(search),
        queryFn: ({ pageParam, signal }) => fetchOptions({ search, page: pageParam }, signal),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
        staleTime: OPTIONS_STALE_TIME,
    });

const selectedCustomerQuery = (id) =>
    queryOptions({
        queryKey: customerKeys.option(id),
        queryFn: async ({ signal }) => (await fetchOptions({ ids: id }, signal)).customers[0] ?? null,
        staleTime: OPTIONS_STALE_TIME,
    });

export const useCustomerOptions = (search, enabled = true) => {
    const query = useInfiniteQuery({ ...customerOptionsQuery(search), enabled, placeholderData: keepPreviousData });
    const customers = useMemo(() => query.data?.pages.flatMap((page) => page.customers) ?? [], [query.data]);

    return {
        customers,
        isLoaded: !!query.data,
        isPlaceholder: query.isPlaceholderData,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
    };
};

export const rememberCustomers = (queryClient, customers) =>
    customers.forEach((customer) => queryClient.setQueryData(customerKeys.option(customer.customer_netsuite_id), customer));

export const useSelectedCustomer = (value, knownCustomers) => {
    const id = !value || typeof value === 'object' ? null : parseInt(value, 10) || null;
    const known = id ? knownCustomers.find((c) => c.customer_netsuite_id === id) : null;
    const { data } = useQuery({ ...selectedCustomerQuery(id), enabled: !!id && !known });

    return known || data || null;
};

export const useCustomerBalance = (customerId) =>
    useQuery({
        queryKey: customerKeys.balance(customerId),
        queryFn: async ({ signal }) => {
            const res = await getCustomerBalance(customerId, signal);
            if (!res?.ack) throw new Error('Unable to load customer balance');
            return res.customer_balance;
        },
        enabled: !!customerId,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });

export const useCustomerSummary = () => {
    const { customers, hasNextPage, isLoaded } = useCustomerOptions('');
    const hasSingleCustomer = isLoaded && customers.length === 1 && !hasNextPage;
    const onlyCustomerId = hasSingleCustomer ? customers[0].customer_netsuite_id : null;

    useEffect(() => {
        if (!isLoaded) return;
        store.dispatch(setHasSingleCustomer(hasSingleCustomer));
        if (onlyCustomerId) store.dispatch(setCustomer(onlyCustomerId));
    }, [isLoaded, hasSingleCustomer, onlyCustomerId]);
};
